import React, { useEffect, useState } from "react";
import {
	Container,
	Typography,
	Button,
	CircularProgress,
	Grid,
	Box,
} from "@material-ui/core";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import CivitatisExperienceRepository from "../../repositories/civitatisExperienceRepository";

const useStyles = makeStyles((theme) => ({
	root: {
		backgroundColor: "#F4F6F8",
		minHeight: "100vh",
		paddingTop: 48,
		paddingBottom: 80,
	},
	header: {
		textAlign: "center",
		marginBottom: 48,
	},
	headerTitle: {
		fontSize: 56,
		fontWeight: 700,
		color: theme.palette.primary.main,
		letterSpacing: "-0.5px",
	},
	mainContainer: {
		display: "flex",
		gap: 32,
		alignItems: "flex-start",
		justifyContent: "center",
		marginBottom: 48,
		[theme.breakpoints.down("md")]: {
			flexDirection: "column",
			gap: 24,
			alignItems: "center",
		},
	},
	successCard: {
		background: "#FFFFFF",
		borderRadius: 16,
		padding: 48,
		width: 550,
		textAlign: "center",
		boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		[theme.breakpoints.down("md")]: {
			width: "100%",
			padding: 32,
		},
	},
	successIcon: {
		fontSize: "96px !important",
		color: theme.palette.success.main,
		marginBottom: 24,
	},
	successTitle: {
		fontSize: 28,
		fontWeight: 700,
		color: "#2D2D2D",
		marginBottom: 24,
		lineHeight: 1.3,
	},
	bookingCode: {
		display: "inline-block",
		background: theme.palette.primary.main,
		color: "#FFFFFF",
		borderRadius: 8,
		padding: "12px 32px",
		fontSize: 20,
		fontWeight: 700,
		letterSpacing: 1.5,
	},
	summaryCard: {
		background: "#FFFFFF",
		borderRadius: 16,
		padding: 32,
		width: 420,
		boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
		[theme.breakpoints.down("md")]: {
			width: "100%",
		},
	},
	summaryTitle: {
		fontSize: 18,
		fontWeight: 700,
		color: "#2D2D2D",
		marginBottom: 24,
	},
	activitySection: {
		display: "flex",
		gap: 12,
		marginBottom: 24,
		alignItems: "center",
	},
	activityImage: {
		width: 80,
		height: 80,
		borderRadius: 8,
		objectFit: "cover",
		backgroundColor: "#E5E7EB",
		flexShrink: 0,
	},
	activityInfo: {
		flex: 1,
	},
	activityName: {
		fontSize: 14,
		fontWeight: 600,
		color: "#2D2D2D",
		lineHeight: 1.4,
	},
	infoSection: {
		marginBottom: 24,
	},
	infoRow: {
		display: "flex",
		justifyContent: "space-between",
		marginBottom: 12,
		fontSize: 13,
	},
	infoLabel: {
		color: "#6B7280",
		fontWeight: 500,
	},
	infoValue: {
		color: "#2D2D2D",
		fontWeight: 600,
	},
	divider: {
		height: 1,
		backgroundColor: "#E5E7EB",
		margin: "20px 0",
	},
	valuesSection: {
		marginTop: 20,
	},
	valueRow: {
		display: "flex",
		justifyContent: "space-between",
		marginBottom: 8,
		fontSize: 13,
	},
	valueLabel: {
		color: "#6B7280",
		fontWeight: 500,
	},
	valueAmount: {
		color: "#2D2D2D",
		fontWeight: 600,
	},
	valueAmountPoints: {
		color: theme.palette.primary.main,
		fontWeight: 700,
	},
	totalRow: {
		display: "flex",
		justifyContent: "space-between",
		marginTop: 12,
		paddingTop: 12,
		borderTop: "2px solid #E5E7EB",
		fontSize: 16,
		fontWeight: 700,
		color: theme.palette.primary.main,
	},
	alertBox: {
		backgroundColor: `${theme.palette.primary.main}15`,
		borderLeft: `4px solid ${theme.palette.primary.main}`,
		borderRadius: 8,
		padding: 12,
		marginTop: 16,
		fontSize: 12,
		color: "#6B7280",
	},
	footerButton: {
		height: 52,
		width: 300,
		borderRadius: 8,
		fontWeight: 700,
		fontSize: 14,
		textTransform: "uppercase",
		letterSpacing: 0.5,
		backgroundColor: theme.palette.primary.main,
		color: "#FFFFFF",
		"&:hover": {
			backgroundColor: theme.palette.primary.dark || theme.palette.primary.main,
			boxShadow: `0 8px 24px ${theme.palette.primary.main}33`,
		},
		[theme.breakpoints.down("md")]: {
			width: "100%",
			maxWidth: 300,
		},
	},
	footerContainer: {
		display: "flex",
		justifyContent: "center",
		marginTop: 48,
	},
	loadingContainer: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		minHeight: 400,
	},
}));

const civitatisRepo = new CivitatisExperienceRepository();

const EntertainmentBookingSuccess = () => {
	const classes = useStyles();
	const history = useHistory();

	const bookingCode = sessionStorage.getItem("@EntertainmentBookingCode");
	const storedResult = (() => {
		try {
			return JSON.parse(sessionStorage.getItem("@EntertainmentBookingResult") || "{}");
		} catch {
			return {};
		}
	})();
	const paymentSummary = (() => {
		try {
			return JSON.parse(sessionStorage.getItem("@EntertainmentPaymentSummary") || "{}");
		} catch {
			return {};
		}
	})();

	// storedResult may be the full booking object or wrapped in { data: ... }
	const normalizedResult = storedResult?.bookingCode ? storedResult : (storedResult?.data ?? storedResult);
	const [details, setDetails] = useState(normalizedResult);
	const [loading, setLoading] = useState(!normalizedResult?.bookingCode && !!bookingCode);
	const searchState = (() => {
		try {
			return JSON.parse(sessionStorage.getItem("entertainmentSearch") || "{}");
		} catch {
			return {};
		}
	})();

	useEffect(() => {
		if (!bookingCode || storedResult?.bookingCode) return;
		civitatisRepo.getBookingDetails(bookingCode).then((data) => {
			if (data) setDetails(data);
			setLoading(false);
		});
	}, [bookingCode]);

	if (!bookingCode) {
		return (
			<div className={classes.root}>
				<Container maxWidth="lg">
					<Box className={classes.header}>
						<Typography className={classes.headerTitle}>Nenhuma reserva encontrada</Typography>
					</Box>
					<Box className={classes.footerContainer}>
						<Button
							variant="contained"
							className={classes.footerButton}
							onClick={() => history.push("/minhas-viagens")}
						>
							Ir para o Início
						</Button>
					</Box>
				</Container>
			</div>
		);
	}

	return (
		<div className={classes.root}>
			<Container maxWidth="lg">
				{/* Header */}
				<Box className={classes.header}>
					<Typography className={classes.headerTitle}>
						Pronto para sua próxima aventura?
					</Typography>
				</Box>

				{/* Main Content */}
				{loading ? (
					<Box className={classes.loadingContainer}>
						<CircularProgress />
					</Box>
				) : (
					<>
						<Box className={classes.mainContainer}>
							{/* Left Column - Success Card */}
							<div className={classes.successCard}>
								<CheckCircleIcon className={classes.successIcon} />
								<Typography className={classes.successTitle}>
									Reserva Realizada com Sucesso!
								</Typography>
								<div className={classes.bookingCode}>{bookingCode}</div>
							</div>

							{/* Right Column - Summary Card */}
							<div className={classes.summaryCard}>
								<Typography className={classes.summaryTitle}>
									Resumo da Reserva
								</Typography>

								{/* Activity Section */}
								{(details.activityName || details.bookingService?.civitatis?.activityName) && (
									<div className={classes.activitySection}>
										{(details.bookingService?.entertainment?.image || details.bookingService?.civitatis?.image || details.image) && (
											<img
												src={details.bookingService?.entertainment?.image || details.bookingService?.civitatis?.image || details.image}
												alt="Activity"
												className={classes.activityImage}
												onError={(e) => {
													e.target.style.display = 'none';
												}}
											/>
										)}
										<div className={classes.activityInfo}>
											<div className={classes.activityName}>
												{details.activityName || details.bookingService?.civitatis?.activityName}
											</div>
										</div>
									</div>
								)}

								{/* Info Section */}
								<div className={classes.infoSection}>
									{details.activityDate || details.bookingService?.civitatis?.activityDate ? (
										<div className={classes.infoRow}>
											<span className={classes.infoLabel}>Data Escolhida</span>
											<span className={classes.infoValue}>
												{details.activityDate
													? new Date(details.activityDate).toLocaleDateString("pt-BR", {
														day: "2-digit",
														month: "long",
														year: "numeric"
													})
													: new Date(details.bookingService?.civitatis?.activityDate).toLocaleDateString("pt-BR", {
														day: "2-digit",
														month: "long",
														year: "numeric"
													})
												}
											</span>
										</div>
									) : null}
									<div className={classes.infoRow}>
										<span className={classes.infoLabel}>Status</span>
										<span style={{ color: "#16A34A", fontWeight: 700, fontSize: 13 }}>
											{details.bookingService?.status || "EMITIDO"}
										</span>
									</div>
								</div>

								<div className={classes.divider} />

								{/* Values Section */}
								<div className={classes.valuesSection}>
									{paymentSummary.pointsTotalValue > 0 && (
										<div className={classes.valueRow}>
											<span className={classes.valueLabel}>Pontos Utilizados</span>
											<span className={classes.valueAmountPoints}>
												{Math.round(paymentSummary.pointsTotalValue).toLocaleString("pt-BR")} pts
											</span>
										</div>
									)}
									{paymentSummary.cashTotalValue > 0 && (
										<div className={classes.valueRow}>
											<span className={classes.valueLabel}>Cartão de Crédito</span>
											<span className={classes.valueAmount}>
												R$ {paymentSummary.cashTotalValue.toFixed(2).replace(".", ",")}
											</span>
										</div>
									)}

									<div className={classes.totalRow}>
										<span>Total</span>
										{paymentSummary.cashTotalValue > 0 && paymentSummary.pointsTotalValue > 0 ? (
											<span style={{ textAlign: "right" }}>
												<div>{Math.round(paymentSummary.pointsTotalValue).toLocaleString("pt-BR")} pts</div>
												<div>+ R$ {paymentSummary.cashTotalValue.toFixed(2).replace(".", ",")}</div>
											</span>
										) : paymentSummary.cashTotalValue > 0 ? (
											<span>R$ {paymentSummary.cashTotalValue.toFixed(2).replace(".", ",")}</span>
										) : (
											<span>{Math.round(paymentSummary.pointsTotalValue || 0).toLocaleString("pt-BR")} pts</span>
										)}
									</div>

									<div className={classes.alertBox}>
										✓ Seu voucher foi enviado para o e-mail cadastrado
									</div>
								</div>
							</div>
						</Box>

						{/* Footer Button */}
						<Box className={classes.footerContainer}>
							<Button
								className={classes.footerButton}
								onClick={() => history.push("/minhas-viagens")}
							>
								Minhas Viagens
							</Button>
						</Box>
					</>
				)}
			</Container>
		</div>
	);
};

export default EntertainmentBookingSuccess;
